import React, { useEffect, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { formatDate } from "../../utils/Utilitats";
import { aplicarValidacio, validarTelefon } from "../../utils/Validacions";
import FitxaRecursos from "./FitxaRecursos";
import { idVehicle } from "./Seleccionarvehicle";
import {
  Camp,
  CampImatge,
  ChipActiu,
  InterruptorActiu,
  Valor,
  avuiISO,
  cridaRecurs,
  estatVenciment,
  useImatgeRecurs,
} from "./RecursosComuns";

const TIPUS_PER_DEFECTE = ["FURGONETA", "TURISME", "CAMIO", "MOTO", "ALTRE"];

const BUIT = {
  Matricula: "",
  Marca: "",
  Model: "",
  Alies: "",
  TipusVehicle: "FURGONETA",
  DataMatriculacio: "",
  DataAlta: "",
  KmActuals: "",
  DataProperaITV: "",
  DataUltimaRevisio: "",
  DataProperaRevisio: "",
  DataVencimentAsseguranca: "",
  NumeroPolissa: "",
  Asseguradora: "",
  TelefonUrgenciaAsseguradora: "",
  Actiu: true,
  Observacions: "",
};

const esActiu = (v) => Number(v.Actiu) === 1;

function formDesDeRegistre(v) {
  const form = { ...BUIT };
  Object.keys(BUIT).forEach((clau) => {
    if (clau === "Actiu") return;
    form[clau] = v[clau] === null || v[clau] === undefined ? "" : String(v[clau]);
  });
  form.Actiu = esActiu(v);
  return form;
}

function Detallsvehicle({ mode, record, llista, meta, onNavigate, onCancel, onCanviarMode, onSave, onEstatCanviat }) {
  const { empresaSeleccionada } = useUserContext();
  const readOnly = mode === "consultar" || mode === "desactivar" || mode === "reactivar";
  const [form, setForm] = useState(BUIT);
  const imatge = useImatgeRecurs();
  const { reinicia } = imatge;

  useEffect(() => {
    if (record && mode !== "nou") {
      setForm(formDesDeRegistre(record));
      reinicia(record.Imatge);
    } else {
      setForm({ ...BUIT, DataAlta: avuiISO() });
      reinicia("");
    }
  }, [record, mode, reinicia]);

  const canvia = (clau) => (e) => setForm((prev) => ({ ...prev, [clau]: e.target.value }));
  const tipusOptions = meta?.tipusVehicle?.length ? meta.tipusVehicle : TIPUS_PER_DEFECTE;

  const validar = () => {
    const errors = [];
    const avisos = [];
    if (!form.Matricula.trim()) errors.push("El camp Matrícula és obligatori.");
    if (form.KmActuals !== "" && !/^\d+$/.test(String(form.KmActuals).trim())) {
      errors.push("Els Km actuals han de ser un nombre enter no negatiu.");
    }
    aplicarValidacio(errors, avisos, "Telèfon d'urgències", validarTelefon(form.TelefonUrgenciaAsseguradora));
    return errors;
  };

  const desar = async () => {
    const errors = validar();
    if (errors.length > 0) {
      alert(`Errors trobats:\n${errors.join("\n")}`);
      return;
    }

    let nomImatge;
    try {
      nomImatge = await imatge.desa();
    } catch (error) {
      alert(`Error en pujar la imatge: ${error.message}`);
      return;
    }

    const payload = {
      ...form,
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      Matricula: form.Matricula.trim(),
      Actiu: form.Actiu ? 1 : 0,
      Imatge: nomImatge,
    };
    if (mode === "modificar") payload.idVehicles = record.idVehicles;

    const resposta = await cridaRecurs(mode === "nou" ? "NouVehicle.php" : "ModificarVehicle.php", payload);
    if (resposta.ok) {
      onSave?.(resposta.data.vehicle);
    } else if (!resposta.silenci) {
      alert(`Error en desar el vehicle: ${resposta.message}`);
    }
  };

  const text = (clau, maxLength, extra = {}) =>
    readOnly ? (
      <Valor>{form[clau]}</Valor>
    ) : (
      <input type="text" className="dv2-input" value={form[clau]} maxLength={maxLength} onChange={canvia(clau)} {...extra} />
    );

  const data = (clau) =>
    readOnly ? (
      <Valor>{form[clau] ? formatDate(form[clau]) : ""}</Valor>
    ) : (
      <input type="date" className="dv2-input" value={form[clau]} onChange={canvia(clau)} />
    );

  // Marca visual (només a la fitxa de consulta) si la data ja ha passat o venç aviat.
  const avisVenciment = (clau) => {
    if (!readOnly) return null;
    const estat = estatVenciment(form[clau]);
    if (!estat) return null;
    return (
      <span className={`dv2-chip ${estat === "caducat" ? "dv2-chip--danger" : "dv2-chip--warn"}`}>
        {estat === "caducat" ? "Vençuda" : "Venç en menys de 30 dies"}
      </span>
    );
  };

  const nomVehicle = [form.Matricula, form.Alies && `(${form.Alies})`].filter(Boolean).join(" ");

  return (
    <FitxaRecursos
      mode={mode}
      record={record}
      llista={llista}
      idDe={idVehicle}
      onNavigate={onNavigate}
      onCancel={onCancel}
      onCanviarMode={onCanviarMode}
      onDesar={desar}
      onEstatCanviat={onEstatCanviat}
      estat={{ endpoint: "ActivarVehicle.php", idCamp: "idVehicles", campActiu: "Actiu" }}
      esActiu={esActiu}
      icona="🚐"
      nomSingular="vehicle"
      nomArticle="el vehicle"
      subtitolNou="Omple la matrícula i les dades bàsiques. La ubicació logística es crea automàticament."
      subtitolRegistre={nomVehicle || "Dades del vehicle"}
      pestanyaAjuda="vehicles"
    >
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Identificació</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Matrícula" required readOnly={readOnly} mida="mig">
            {text("Matricula", 15, { autoFocus: mode === "nou" })}
          </Camp>
          <Camp label="Àlies" readOnly={readOnly} mida="mig">
            {text("Alies", 50)}
          </Camp>
          <Camp label="Marca" readOnly={readOnly} mida="mig">
            {text("Marca", 50)}
          </Camp>
          <Camp label="Model" readOnly={readOnly} mida="llarg">
            {text("Model", 100)}
          </Camp>
          <Camp label="Tipus de vehicle" readOnly={readOnly} mida="mig">
            {readOnly ? (
              <Valor>{mapToPresentable(form.TipusVehicle, mapejos.tipusVehicle)}</Valor>
            ) : (
              <select className="dv2-select" value={form.TipusVehicle} onChange={canvia("TipusVehicle")}>
                <option value="">—</option>
                {tipusOptions.map((t) => (
                  <option key={t} value={t}>
                    {mapToPresentable(t, mapejos.tipusVehicle)}
                  </option>
                ))}
              </select>
            )}
          </Camp>
          <Camp label="Imatge" readOnly={readOnly} mida="ample">
            <CampImatge imatge={imatge} readOnly={readOnly} alt={`Imatge del vehicle ${form.Matricula}`} icona="🚐" />
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Dates i manteniment</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Data de matriculació" readOnly={readOnly}>
            {data("DataMatriculacio")}
          </Camp>
          <Camp label="Data d'alta" readOnly={readOnly}>
            {data("DataAlta")}
          </Camp>
          <Camp label="Km actuals" readOnly={readOnly}>
            {readOnly ? (
              <Valor>{form.KmActuals === "" ? "" : `${Number(form.KmActuals).toLocaleString("ca-ES")} km`}</Valor>
            ) : (
              <input type="number" min="0" step="1" className="dv2-input" value={form.KmActuals} onChange={canvia("KmActuals")} />
            )}
          </Camp>
          <Camp label="Propera ITV" readOnly={readOnly}>
            {data("DataProperaITV")}
            {avisVenciment("DataProperaITV")}
          </Camp>
          <Camp label="Última revisió" readOnly={readOnly}>
            {data("DataUltimaRevisio")}
          </Camp>
          <Camp label="Propera revisió" readOnly={readOnly}>
            {data("DataProperaRevisio")}
            {avisVenciment("DataProperaRevisio")}
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">3</span>
          <h2 className="dv2-card-title">Assegurança</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Asseguradora" readOnly={readOnly} mida="llarg">
            {text("Asseguradora", 100)}
          </Camp>
          <Camp label="Número de pòlissa" readOnly={readOnly}>
            {text("NumeroPolissa", 50)}
          </Camp>
          <Camp label="Venciment assegurança" readOnly={readOnly}>
            {data("DataVencimentAsseguranca")}
            {avisVenciment("DataVencimentAsseguranca")}
          </Camp>
          <Camp label="Telèfon d'urgències" readOnly={readOnly}>
            {readOnly ? (
              form.TelefonUrgenciaAsseguradora ? (
                <a className="dv2-value" href={`tel:${form.TelefonUrgenciaAsseguradora.replace(/[^\d+]/g, "")}`}>
                  {form.TelefonUrgenciaAsseguradora}
                </a>
              ) : (
                <Valor>{""}</Valor>
              )
            ) : (
              <input
                type="tel"
                className="dv2-input"
                value={form.TelefonUrgenciaAsseguradora}
                maxLength={30}
                onChange={canvia("TelefonUrgenciaAsseguradora")}
              />
            )}
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">4</span>
          <h2 className="dv2-card-title">Altres</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Actiu" readOnly={readOnly} mida="curt">
            {mode === "consultar" || mode === "desactivar" || mode === "reactivar" ? (
              <ChipActiu actiu={form.Actiu} />
            ) : (
              <InterruptorActiu valor={form.Actiu} onChange={(v) => setForm((prev) => ({ ...prev, Actiu: v }))} />
            )}
          </Camp>
          <Camp label="Observacions" readOnly={readOnly} mida="ample">
            {readOnly ? (
              <Valor>{form.Observacions}</Valor>
            ) : (
              <textarea className="dv2-textarea" value={form.Observacions} maxLength={255} onChange={canvia("Observacions")} />
            )}
          </Camp>
        </div>
      </section>
    </FitxaRecursos>
  );
}

export default Detallsvehicle;
